import React, {
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
} from "react";
import { Textarea } from "@fluentui/react-components";

import styles from "./QuestionInput.module.css";

export interface QuestionInputHandle {
  /** Trigger send programmatically (used by external SendButton) */
  send: () => void;
  /** Whether the input currently has sendable content */
  canSend: () => boolean;
}

interface Props {
  onSend: (question: string) => void;
  disabled: boolean;
  placeholder?: string;
  default_question: string;
  sessionState: string;
  onFilesAdded?: (files: File[]) => void;
  /** Called whenever sendability changes so the parent can update SendButton */
  onCanSendChange?: (canSend: boolean) => void;
}

export const QuestionInput = forwardRef<QuestionInputHandle, Props>(
  (
    {
      onSend,
      disabled,
      placeholder,
      default_question,
      sessionState,
      onFilesAdded,
      onCanSendChange,
    },
    ref,
  ) => {
    const [question, setQuestion] = useState(default_question);
    const [updatedPlaceholder, setUpdatedPlaceHolder] = useState(placeholder);
    const [isDragging, setIsDragging] = useState(false);

    const textareaRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
      setUpdatedPlaceHolder(placeholder);
    }, [placeholder]);

    useEffect(() => {
      setQuestion(default_question);
    }, [default_question]);

    useEffect(() => {
      setQuestion(default_question);
      setUpdatedPlaceHolder(placeholder);
    }, [sessionState]);

    // Notify parent whenever sendability changes
    useEffect(() => {
      onCanSendChange?.(!disabled && !!question.trim());
    }, [question, disabled, onCanSendChange]);

    const sendQuestion = () => {
      if (disabled || !question.trim()) return;

      onSend(question);
      setQuestion("");

      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
      }
    };

    // Expose send() and canSend() to parent via ref
    useImperativeHandle(ref, () => ({
      send: sendQuestion,
      canSend: () => !disabled && !!question.trim(),
    }));

    const onEnterPress = (ev: React.KeyboardEvent<Element>) => {
      if (ev.key === "Enter" && !ev.shiftKey) {
        ev.preventDefault();
        sendQuestion();
      }
    };

    const handlePaste = async (
      ev: React.ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      if (!onFilesAdded) return;

      const clipboardData = ev.clipboardData;
      if (!clipboardData) return;

      const text = clipboardData.getData("text/plain");
      if (text && text.trim().length > 0) return;

      const items = clipboardData.items;
      if (!items || items.length === 0) return;

      const files: File[] = [];
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === "file") {
          const file = item.getAsFile();
          if (file) {
            const mime = file.type;
            const name = (file as any).name || "";
            const isLikelyRealFile =
              /^(application\/pdf|image\/|text\/|application\/msword)/.test(
                mime,
              ) || /\.(pdf|docx?|pptx?|txt|md|png|jpe?g)$/i.test(name);
            if (isLikelyRealFile) files.push(file);
          }
        }
      }

      if (files.length > 0) {
        ev.preventDefault();
        onFilesAdded(files);
      }
    };

    const handleDragOver = (ev: React.DragEvent<HTMLDivElement>) => {
      ev.preventDefault();
      if (!isDragging) setIsDragging(true);
    };

    const handleDragLeave = (ev: React.DragEvent<HTMLDivElement>) => {
      ev.preventDefault();
      setIsDragging(false);
    };

    const handleDrop = (ev: React.DragEvent<HTMLDivElement>) => {
      ev.preventDefault();
      setIsDragging(false);
      if (!onFilesAdded) return;

      const dt = ev.dataTransfer;
      if (!dt || !dt.files || dt.files.length === 0) return;

      const files: File[] = [];
      for (let i = 0; i < dt.files.length; i++) {
        files.push(dt.files[i]);
      }
      if (files.length > 0) onFilesAdded(files);
    };

    return (
      <div
        className={styles.inputContainer}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div
          className={`${styles.questionInputWrapper} ${
            isDragging ? styles.dragActive : ""
          }`}
        >
          <Textarea
            className={styles.textAreaRoot}
            textarea={{
              className: styles.textAreaInput,
              ref: textareaRef,
              onInput: (e: any) => {
                e.target.style.height = "auto";
                e.target.style.height = `${e.target.scrollHeight}px`;
              },
            }}
            placeholder={updatedPlaceholder}
            resize="none"
            appearance="outline"
            value={question}
            onChange={(_, data) => {
              if (!data.value) {
                setQuestion("");
              } else if (data.value.length <= 80000) {
                setQuestion(data.value);
              }
            }}
            onKeyDown={onEnterPress}
            autoComplete="off"
            onPaste={handlePaste}
          />
        </div>
      </div>
    );
  },
);

QuestionInput.displayName = "QuestionInput";
